import { OPMCalculatorResultsResponse } from 'api';
import { largeCurrencyFormat } from 'common/formats/formats';
import { SpreadsheetConfig } from 'components/ScalarSpreadsheet/utilities/SpreadsheetConfig';
import { alphabetGenerator } from 'utilities/alphabet-utilities';
import {
  ALLOCATION_OF_INCREMENTAL_OPTION_VALUE_TITLE,
  getOPMCalculatorAllocationWorkbookKey,
  HEADER_ROW_CLASS_NAME,
  TOTAL_ROW_TITLE,
} from '../constants';
import { breakpointRangeHeader } from './utils';

interface CreateAllocationConfigParams {
  panelId: string;
  results: OPMCalculatorResultsResponse;
  formatCurrency: (value: number) => string;
}

const HEADER_ROW = 1;

/**
 * "Allocation of Incremental Option Value…" -- one column per breakpoint slice, one row per
 * security, plus a totals row.
 *
 * Unlike the ladder there is no baseline column here: every column is a real slice, and the
 * totals row is that slice's `option_increment`, which the per-security amounts sum to.
 *
 * Security names come from `rowConfig[].value` and are rendered in the spreadsheet's own
 * titles column, so column A is the first slice. Row 1 is the header row.
 */
const createAllocationConfig = ({ panelId, results, formatCurrency }: CreateAllocationConfigParams) => {
  const { breakpoints, distribution, securities } = results;
  const alphabet = alphabetGenerator([], breakpoints.length);

  const rowConfig = [
    {
      value: ALLOCATION_OF_INCREMENTAL_OPTION_VALUE_TITLE,
      className: HEADER_ROW_CLASS_NAME,
      gridType: 'string',
      ignoreRowCopy: true,
      largeHeader: true,
      readOnly: true,
    },
    ...securities.map(security => ({
      value: security.name,
      gridType: 'string',
      ignoreRowCopy: true,
      readOnly: true,
    })),
    {
      value: TOTAL_ROW_TITLE,
      gridType: 'string',
      ignoreRowCopy: true,
      readOnly: true,
      titleClassName: 'subtitle',
    },
  ];

  // Securities start on row 2, below the header row.
  const securityRow = (index: number) => index + 2;
  const totalRow = securities.length + 2;

  const parser = () => {
    let cells: Record<string, unknown> = {};

    const amountCell = (key: string, value: number) => ({
      key,
      value,
      format: largeCurrencyFormat,
      gridType: 'number',
      readOnly: true,
    });

    distribution.forEach((slice, sliceIndex) => {
      const column = alphabet[sliceIndex];
      const breakpoint = breakpoints[sliceIndex];

      // The service orders each slice's series the same way across slices, but match on
      // security id rather than position so a reordering upstream cannot silently misalign
      // a row with the wrong security.
      const amountBySecurityId = new Map<number, number>(
        slice.series.map(entry => [
          Number((entry.security as unknown as { id: number })?.id),
          entry.distribution_amount,
        ])
      );

      const headerKey = `${column}${HEADER_ROW}`;
      cells = {
        ...cells,
        [headerKey]: {
          key: headerKey,
          value: breakpointRangeHeader({
            breakpoint,
            previousBreakpoint: sliceIndex === 0 ? undefined : breakpoints[sliceIndex - 1],
            formatCurrency,
          }),
          gridType: 'string',
          readOnly: true,
        },
      };

      securities.forEach((security, securityIndex) => {
        const key = `${column}${securityRow(securityIndex)}`;
        cells = { ...cells, [key]: amountCell(key, amountBySecurityId.get(Number(security.id)) ?? 0) };
      });

      const totalKey = `${column}${totalRow}`;
      cells = { ...cells, [totalKey]: amountCell(totalKey, slice.option_increment) };
    });

    return cells;
  };

  return new SpreadsheetConfig({
    name: getOPMCalculatorAllocationWorkbookKey(panelId),
    parser,
    rowConfig,
    columns: breakpoints.map(() => ({})),
    tableData: { results },
    tableTerms: {
      tableName: ALLOCATION_OF_INCREMENTAL_OPTION_VALUE_TITLE,
      tableSlug: 'opm-calculator-allocation',
    },
    showToolbar: false,
    showTotalColumn: false,
    currencyFormatter: false,
    unitsFormatter: false,
    allowCopyColumn: false,
    allowReorderColumns: false,
    allowConfirmAndDeleteColumn: false,
  } as unknown as ConstructorParameters<typeof SpreadsheetConfig>[0]);
};

export default createAllocationConfig;
